<template>
  <DrawerModal
    class="SeasonModalView"
    :aria-label="formattedSeasonName ? `Season details for ${formattedSeasonName}` : 'Season details'"
    :aria-busy="!isSeasonLoaded"
    :label="isSeasonLoaded && !isHeroVisible && !isTransitioning ? (formattedSeasonName ?? 'New season') : undefined"
  >
    <div class="SeasonModalView__layout">
      <SeasonHero
        v-if="season"
        v-bind="{ season }"
        ref="heroRef"
      />
      <template v-if="isCommishForSeason">
        <Accordion
          :isExpanded="!season?.completed_at"
        >
          <div
            class="SeasonModalView__buttonContainer"
          >
            <SeasonScheduleRunButtonView
              v-if="season"
              v-bind="{ season }"
            />
            <FormButton
              iconName="peoplePlus"
              @click="openModal({name: RouteName.seasonEditRoster, params: {seasonId: seasonId}})"
            >
              Manage the roster
            </FormButton>
          </div>
        </Accordion>
        <div
          class="SeasonModalView__buttonContainer"
        >
          <FormButton
            iconName="pencil"
            @click="openModal({name: RouteName.seasonEdit, params: {seasonId: seasonId}})"
          >
            Edit the season
          </FormButton>
          <FormButton
            iconName="check"
            :disabled="isCompletingSeason"
            :aria-busy="isCompletingSeason"
            @click="handleCompleteSeasonClick()"
          >
            <span v-if="!season?.completed_at && !isCompletingSeason">Complete the season</span>
            <span v-else-if="!season?.completed_at && isCompletingSeason">Completing the season…</span>
            <span v-else-if="season?.completed_at && !isCompletingSeason">Reactivate the season</span>
            <span v-else-if="season?.completed_at && isCompletingSeason">Reactivating the season…</span>
          </FormButton>
          <FormButton
            iconName="trash"
            variant="destructive"
            :disabled="isDeletingSeason"
            :aria-busy="isDeletingSeason"
            @click="handleDeleteSeasonClick()"
          >
            Delete the season
          </FormButton>
        </div>
      </template>
      <SeasonScheduleScreenView
        v-bind="{ seasonId }"
      />
      <SeasonPlayerScreenView
        v-bind="{ seasonId }"
      />
    </div>
  </DrawerModal>
</template>

<script lang="ts" setup>
import Accordion from "@/components/Accordion.vue";
import DrawerModal from "@/components/DrawerModal.vue";
import FormButton from "@/components/FormButton.vue";
import SeasonHero from "@/components/SeasonHero.vue";
import useIntersectionDetection from "@/composables/useIntersectionDetection";
import useSeasonSchedule from "@/composables/useSeasonSchedule";
import { supabaseClient } from "@/lib/database/supabase.ts";
import { RouteName } from "@/lib/router/routes.ts";
import useCurrentPlayerStore from "@/stores/useCurrentPlayerStore.ts";
import useModalStore from "@/stores/useModalStore.ts";
import { default as useSeasonStore } from "@/stores/useSeasonStore";
import { storeToRefs } from "pinia";
import { computed, ref, useTemplateRef, watch } from "vue";
import SeasonScheduleRunButtonView from "../SeasonScheduleRunButtonView.vue";
import SeasonPlayerScreenView from "./SeasonPlayerScreenView.vue";
import SeasonScheduleScreenView from "./SeasonScheduleScreenView.vue";

const {
  seasonId,
  isTransitioning = false,
} = defineProps<{
  seasonId: string,
  isTransitioning?: boolean,
}>();

const {
  openModal,
  closeActiveModal,
} = useModalStore();

const {
  crewMemberships,
} = storeToRefs(useCurrentPlayerStore());

const {
  fetchSeason,
  getSeason,
  updateSeason,
  removeSeason,
} = useSeasonStore();

const heroRef = useTemplateRef("heroRef");
const {
  isIntersecting: isHeroVisible,
} = useIntersectionDetection(heroRef);

const isSeasonLoaded = ref(false);
const isCompletingSeason = ref(false);
const isDeletingSeason = ref(false);

const season = computed(() => {
  return getSeason(seasonId);
});

const isCommishForSeason = computed(() => {
  return crewMemberships.value.some((membership) => membership.crew_id === season.value?.crew_id && membership.role === "commissioner");
});

const {
  formattedSeasonName,
} = useSeasonSchedule(season);

watch(() => seasonId, async () => {
  isSeasonLoaded.value = false;
  try {
    await fetchSeason(seasonId);
  } catch (err) {
    alert((err as Error).message);
  }

  isSeasonLoaded.value = true;
}, {
  immediate: true,
});

async function handleCompleteSeasonClick () {
  isCompletingSeason.value = true;

  const {
    data: updatedSeason,
    error,
  } = await supabaseClient.from("seasons")
    .update({ completed_at: season.value?.completed_at ? null : new Date().toISOString() })
    .eq("id", seasonId)
    .select("completed_at")
    .maybeSingle();

  if (error) {
    alert((error as Error).message);
  }

  if (updatedSeason) {
    updateSeason(seasonId, { completed_at: updatedSeason.completed_at });
  }

  isCompletingSeason.value = false;
}

async function handleDeleteSeasonClick () {
  if (!confirm("Are you sure you want to delete this season?\n\nYou can’t run this back.")) return;

  isDeletingSeason.value = true;

  const {
    count,
    error,
  } = await supabaseClient
    .from("seasons")
    .delete({ count: "exact" })
    .eq("id", seasonId)
    .maybeSingle();

  if (error) {
    alert((error as Error).message);
  } else if (count === 0) {
    alert("You don’t have permission to delete this season.");
  } else {
    removeSeason(seasonId);
    closeActiveModal();
  }

  isDeletingSeason.value = false;
}
</script>

<style lang="scss" scoped>
.SeasonModalView__layout {
  min-height: 100%;
  padding-bottom: var(--safeAreaInsetBottom);
  background-color: var(--themeColorSurface);
}

.SeasonModalView__buttonContainer {
  display: grid;
  gap: 16px;
  padding: 16px;

  border-bottom: solid 1px var(--themeColorNeutral100);
}
</style>
